import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export function useAbsenceFormValidation(open: boolean, errors: Record<string, string | undefined>, failure: string, scope?: { key: string; fields: readonly string[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [attempted, setAttempted] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [reset, setReset] = useState<Record<string, boolean>>({});
  const scopeKey = useRef(scope?.key);
  const resetFields = useCallback((names: readonly string[]) => {
    setTouched((previous) => ({ ...previous, ...Object.fromEntries(names.map((name) => [name, false])) }));
    setReset((previous) => ({ ...previous, ...Object.fromEntries(names.map((name) => [name, true])) }));
  }, []);
  useLayoutEffect(() => {
    if (scopeKey.current === scope?.key) return;
    scopeKey.current = scope?.key;
    if (scope) resetFields(scope.fields);
  }, [scope?.key, scope?.fields, resetFields]);
  const [focusRequest, setFocusRequest] = useState(0);
  useLayoutEffect(() => {
    if (!focusRequest) return;
    const invalidField = ref.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    invalidField?.focus({ preventScroll: true });
    invalidField?.scrollIntoView({ block: 'center' });
  }, [focusRequest]);
  useEffect(() => { if (open) { setAttempted(false); setTouched({}); setReset({}); } }, [open]);
  useEffect(() => {
    if (!failure) return;
    const alert = ref.current?.querySelector<HTMLElement>('[role="alert"]');
    alert?.setAttribute('tabindex', '-1');
    alert?.focus();
  }, [failure]);
  return {
    ref,
    field(name: string, helperText?: string, externalError?: string) {
      const visibleError = externalError ?? ((attempted && !reset[name]) || touched[name] ? errors[name] : undefined);
      return { error: Boolean(visibleError), helperText: visibleError ?? helperText, onBlur: () => setTouched((previous) => ({ ...previous, [name]: true })) };
    },
    validate() {
      setAttempted(true);
      setReset({});
      if (!Object.values(errors).some(Boolean)) return true;
      setFocusRequest((current) => current + 1);
      return false;
    },
  };
}
